import { useCallback, useEffect, useState } from 'react'
import type { AppNotification } from '../lib/notifications'
import {
    fetchNotifications,
    fetchUnreadCount,
    markNotificationRead,
    markAllNotificationsRead,
} from '../lib/notifications'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import { toast } from 'sonner'

interface NotificationsState {
    notifications: AppNotification[]
    unreadCount: number
    loading: boolean
    error: string | null
    markRead: (id: string) => Promise<void>
    markAllRead: () => Promise<void>
    refresh: () => void
}

export function useNotifications(): NotificationsState {
    const { profile } = useAuth()
    const [notifications, setNotifications] = useState<AppNotification[]>([])
    const [unreadCount, setUnreadCount] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)

        const [notifResult, count] = await Promise.all([
            fetchNotifications(),
            fetchUnreadCount(),
        ])

        if (notifResult.error) setError(notifResult.error)
        else setNotifications(notifResult.data)

        setUnreadCount(count)
        setLoading(false)
    }, [])

    useEffect(() => {
        load()

        if (!profile?.id) return;

        // Setup real-time subscription for THIS specific seller
        const channel = supabase
            .channel('public:notifications')
            .on(
                'postgres_changes',
                { 
                    event: 'INSERT', 
                    schema: 'public', 
                    table: 'notifications',
                    filter: `seller_id=eq.${profile.id}`
                },
                (payload) => {
                    load() // Reload notifications on new insert
                    // Trigger a toast so the seller sees it immediately anywhere in the app
                    if (payload.new && payload.new.title) {
                        toast.info(payload.new.title, { description: payload.new.message })
                    }
                }
            )
            .subscribe()

        return () => {
            supabase.removeChannel(channel)
        }
    }, [load, profile?.id])

    const markRead = useCallback(async (id: string) => {
        // Optimistic update
        setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, read: true } : n))
        )
        setUnreadCount((c) => Math.max(0, c - 1))
        await markNotificationRead(id)
    }, [])

    const markAllRead = useCallback(async () => {
        // Optimistic update
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
        setUnreadCount(0)
        const { error } = await markAllNotificationsRead()
        if (error) setError(error)
    }, [])

    return { notifications, unreadCount, loading, error, markRead, markAllRead, refresh: load }
}
